var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { compose, margin, width } from 'styled-system';
import { EyeClosedIcon, EyeIcon } from '../../icons';
import { InnerInput as Input } from '../Input/InnerInput';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import { useGeneratedId } from '../../utils/hooks/useGeneratedId';
import { getSemanticValue } from '../../utils/cssVariables';
import { ToggleButton } from './ToggleButton';
import { TOGGLE_MODE_BUTTON_WIDTH } from './constants';
import { extractWidthProps, extractWrapperMarginProps } from '../../utils/extractProps';
const PasswordWrapper = styled.div.withConfig({ displayName: "PasswordWrapper", componentId: "sc-1qd8ahd" }) `
    display: inline-block;
    position: relative;
    box-sizing: border-box;

    ${compose(margin, width)}

    input {
        /* avoid text under the toggle mode button */
        padding-right: ${TOGGLE_MODE_BUTTON_WIDTH};
    }
`;
const defaultAriaStrings = {
    showPasswordButton: 'Show password',
    hidePasswordButton: 'Hide password',
    messagePasswordIsHidden: 'Your password is hidden',
    messagePasswordIsShown: 'Your password is shown'
};
const Password = forwardRef((_a, ref) => {
    var { purpose = 'login', id, disabled, size = 'medium', variant = 'boxed', ariaStrings = {} } = _a, rest = __rest(_a, ["purpose", "id", "disabled", "size", "variant", "ariaStrings"]);
    const [isHidden, setIsHidden] = useState(true);
    const aria = Object.assign(Object.assign({}, defaultAriaStrings), ariaStrings);
    const inputId = useGeneratedId(id);
    const { marginProps, restProps: withoutMargin } = extractWrapperMarginProps(rest);
    const { widthProps, restProps } = extractWidthProps(withoutMargin);
    const inputRef = useRef();
    useImperativeHandle(ref, () => inputRef.current, []);
    return (React.createElement(PasswordWrapper, Object.assign({}, widthProps, marginProps),
        React.createElement(Input, Object.assign({}, restProps, { width: "100%", id: inputId, size: size, variant: variant, disabled: disabled, ref: inputRef, type: isHidden ? 'password' : 'text', autoComplete: purpose === 'new-password' ? 'new-password' : 'off' })),
        !disabled ? (React.createElement(React.Fragment, null,
            React.createElement(ToggleButton, { size: size, variant: variant, type: "button", onClick: () => {
                    setIsHidden(prevValue => !prevValue);
                    if (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current)
                        inputRef.current.focus();
                }, "aria-controls": inputId, "aria-label": isHidden ? aria.showPasswordButton : aria.hidePasswordButton, style: {
                    '--wave-c-password-color': getSemanticValue('foreground-neutral-emphasized'),
                    '--wave-c-password-color-hover': getSemanticValue('foreground-primary')
                } }, isHidden ? React.createElement(EyeIcon, null) : React.createElement(EyeClosedIcon, null)),
            React.createElement(VisuallyHidden, { as: "span", "aria-live": "polite" }, isHidden ? aria.messagePasswordIsHidden : aria.messagePasswordIsShown))) : undefined));
});
export { Password };
